import { apiRequest, authenticatedFetch, ApiError } from './client'
import type {
  SilkAIModel,
  SilkAISubmitBody,
  SilkAITask,
  SilkAITaskList,
  SilkAITaskType,
  SilkScriptDocumentRecognizeResult,
} from './types'

function submit(type: SilkAITaskType, body: SilkAISubmitBody) {
  return apiRequest<SilkAITask>(`/api/v1/ai/${type}`, { method: 'POST', body })
}

export interface AIModelProbeResult {
  model_id: string
  model_name: string
  provider: string
  text: string
  reported_model_name?: string
  latency_ms: number
  cost: number
}

export function extractTextOutput(output?: Record<string, unknown>): string {
  if (!output) return ''
  for (const key of ['text', 'content', 'output_text', 'message', 'response']) {
    const v = output[key]
    if (typeof v === 'string' && v.trim()) return v
  }
  return ''
}

export function extractImageUrl(output?: Record<string, unknown>): string | undefined {
  if (!output) return undefined
  const urls = output.image_urls
  if (Array.isArray(urls) && urls.length > 0) return String(urls[0])
  if (typeof output.image_url === 'string' && output.image_url.trim()) return output.image_url
  if (typeof output.url === 'string' && output.url.trim()) return output.url
  return undefined
}

export function extractVideoUrl(output?: Record<string, unknown>): string | undefined {
  if (!output) return undefined
  const urls = output.video_urls
  if (Array.isArray(urls) && urls.length > 0) return String(urls[0])
  if (typeof output.video_url === 'string') return output.video_url
  return undefined
}

export function extractAudioUrl(output?: Record<string, unknown>): string | undefined {
  if (!output) return undefined
  const urls = output.audio_urls
  if (Array.isArray(urls) && urls.length > 0) return String(urls[0])
  if (typeof output.audio_url === 'string') return output.audio_url
  return undefined
}

/** GET /api/v1/ai/dashscope/cloned-voices 单项 */
export interface DashScopeClonedVoice {
  voice: string
  target_model?: string
  language?: string
  created_at?: string
}

/** GET /api/v1/ai/dashscope/designed-voices 单项 */
export interface DashScopeDesignedVoice {
  voice: string
  target_model?: string
  voice_prompt?: string
  language?: string
  created_at?: string
}

export interface DashScopeVoiceListResult<T> {
  total: number
  items: T[]
  page_index?: number
  page_size?: number
}

export interface DashScopeDesignedVoiceCreateBody {
  model_id: string
  voice_prompt: string
  preview_text?: string
  preferred_name?: string
}

export interface DashScopeDesignedVoiceCreateResult {
  voice: string
  target_model?: string
  preview_audio?: string
}

export const aiApi = {
  /** 租户模型目录（公开读；生成任务仍须登录） */
  models: async (params?: { limit?: number; offset?: number }) => {
    const res = await apiRequest<{ items: SilkAIModel[] | null; total: number }>('/api/v1/ai/models', {
      auth: false,
      query:
        params?.limit != null || params?.offset != null
          ? { limit: params?.limit ?? 100, offset: params?.offset ?? 0 }
          : { all: 'true' },
    })
    // Go 空切片常序列化为 null；调用方一律当数组用
    return Array.isArray(res.items) ? res.items : []
  },

  /** 同步文本探针（剧本编辑器模型连通性测试） */
  probeText: (body?: { model_id?: string }) =>
    apiRequest<AIModelProbeResult>('/api/v1/ai/text/probe', { method: 'POST', body: body ?? {} }),

  submitText: (body: SilkAISubmitBody) => submit('text', body),
  submitImage: (body: SilkAISubmitBody) => submit('image', body),
  submitVideo: (body: SilkAISubmitBody) => submit('video', body),
  submitAudio: (body: SilkAISubmitBody) => submit('audio', body),

  /** Qwen 复刻音色列表（画布克隆 Tab） */
  listDashScopeClonedVoices: (params: {
    model_id: string
    page_index?: number
    page_size?: number
  }) =>
    apiRequest<DashScopeVoiceListResult<DashScopeClonedVoice>>('/api/v1/ai/dashscope/cloned-voices', {
      query: {
        model_id: params.model_id,
        page_index: params.page_index ?? 0,
        page_size: params.page_size ?? 50,
      },
    }),

  /** Qwen 设计音色列表（画布设计 Tab） */
  listDashScopeDesignedVoices: (params: {
    model_id: string
    page_index?: number
    page_size?: number
  }) =>
    apiRequest<DashScopeVoiceListResult<DashScopeDesignedVoice>>('/api/v1/ai/dashscope/designed-voices', {
      query: {
        model_id: params.model_id,
        page_index: params.page_index ?? 0,
        page_size: params.page_size ?? 50,
      },
    }),

  /** 根据文字描述创建设计音色 */
  createDashScopeDesignedVoice: (body: DashScopeDesignedVoiceCreateBody) =>
    apiRequest<DashScopeDesignedVoiceCreateResult>('/api/v1/ai/dashscope/designed-voices', {
      method: 'POST',
      body,
    }),

  get: (id: string) => apiRequest<SilkAITask>(`/api/v1/ai/tasks/${encodeURIComponent(id)}`),

  /** creation-studio 兼容别名 */
  getTask: (id: string) => apiRequest<SilkAITask>(`/api/v1/ai/tasks/${encodeURIComponent(id)}`),

  list: (params?: { limit?: number; offset?: number }) =>
    apiRequest<SilkAITaskList>('/api/v1/ai/tasks', { query: params }),

  delete: (id: string) =>
    apiRequest<{ ok: boolean }>(`/api/v1/ai/tasks/${encodeURIComponent(id)}`, { method: 'DELETE' }),

  recognizeScriptDocument: async (
    file: File,
    modelId?: string,
  ): Promise<SilkScriptDocumentRecognizeResult> => {
    const form = new FormData()
    form.append('file', file)
    if (modelId?.trim()) form.append('model_id', modelId.trim())
    const res = await authenticatedFetch('/api/v1/ai/recognize-script-document', {
      method: 'POST',
      body: form,
    })
    const json = await res.json().catch(() => ({}))
    if (!res.ok || (json && json.code !== undefined && json.code !== 0)) {
      throw new ApiError({
        code: json?.code ?? 0,
        status: res.status,
        message: json?.message || `recognize failed: ${res.status}`,
        payload: json,
      })
    }
    return json.data as SilkScriptDocumentRecognizeResult
  },
}
